import React, { useState, useEffect } from 'react';
import { Calendar, X, Upload, Save, Edit, FileText, Plus, Settings, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { EditModal, AddProjectModal, AddHolidayModal } from './Modals';
import { CalendarGrid } from './CalendarGrid';
import { getRequest, postRequest } from '../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReusableDeleteDialog from '../common/ReusableDeleteDialog';

const ShipyardHolidayCalendar = () => {
  const [workdays, setWorkdays] = useState([]);
  const [workingHours, setWorkingHours] = useState({ start: "09:00", end: "17:00" });
  const [holidays, setHolidays] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [calendarName, setCalendarName] = useState('');
  const [calendarId, setCalendarId] = useState(null);
  const [fileName, setFileName] = useState('');
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddHolidayModal, setShowAddHolidayModal] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingCalendar, setIsFetchingCalendar] = useState(false);
  const [selectedDateForHoliday, setSelectedDateForHoliday] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('master/projects/');
        if (response?.status === 'success') {
          setProjects(response.data);
          if (response.data.length > 0) {
            setSelectedProject(response.data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to load projects');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject && !isFetchingCalendar) {
      fetchCalendarDetails(selectedProject);
    }
  }, [selectedProject]);

  const fetchCalendarDetails = async (projectId) => {
    try {
      setIsFetchingCalendar(true);
      const calendarResponse = await getRequest(`transaction/calendar-detail/?project_id=${projectId}`);
      
      if (calendarResponse) {
        const holidaysResponse = await getRequest(`https://pms-api.ilizien-projects-cdf.in/transaction/holiday/`);
        
        let holidaysData = [];
        if (holidaysResponse && holidaysResponse.status === "success") {
          holidaysData = holidaysResponse.data.filter(h => h.calendar === calendarResponse.id);
        }
  
        setCalendarId(calendarResponse.id);
        setCalendarName(calendarResponse.name);
        setWorkdays(calendarResponse.working_days || []);
        setWorkingHours(calendarResponse.working_hours || { start: "09:00", end: "17:00" });
        setHolidays(holidaysData);
        toast.success('Calendar loaded successfully');
      }
    } catch (error) {
      console.error('Error fetching calendar details:', error);
      toast.error('Failed to load calendar details');
    } finally {
      setIsFetchingCalendar(false);
    }
  };
  
  const removeDuplicateHolidays = (holidaysList) => {
    const uniqueMap = new Map();
    holidaysList.forEach(holiday => {
      if (!uniqueMap.has(holiday.date) || (holiday.id && !uniqueMap.get(holiday.date).id)) {
        uniqueMap.set(holiday.date, holiday);
      }
    });
    return Array.from(uniqueMap.values());
  };

  const handleDayClick = (dateString) => {
    if (!isEditing) return;
    setSelectedDateForHoliday(dateString);
    const holiday = holidays.find(h => h.date === dateString);
    
    if (holiday) {
      if (!holiday.id) {
        toast.info('Unsaved holiday will be removed', {
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setHolidays(prev => prev.filter(h => h.date !== dateString));
        return;
      }
      setShowAddHolidayModal(true);
    } else {
      setShowAddHolidayModal(true);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = JSON.parse(event.target.result);
        if (content?.holidays) {
          const combinedHolidays = [...holidays, ...content.holidays];
          setHolidays(removeDuplicateHolidays(combinedHolidays));
          toast.success(`${content.holidays.length} holidays imported successfully`);
        }
      } catch (error) {
        toast.error('Invalid file format. Please upload a valid JSON file.');
      }
    };
    reader.readAsText(file);
  };

  const handleSaveSettings = async (newSettings) => {
    try {
      const payload = {
        project: selectedProject,
        name: newSettings.name || calendarName,
        working_days: newSettings.workdays || workdays,
        working_hours: newSettings.workingHours || workingHours
      };
  
      let response;
      if (calendarId) {
        // Replace put with post + id
        response = await postRequest('transaction/calendar/', {
          ...payload,
          id: calendarId
        });
      } else {
        response = await postRequest('transaction/calendar/', payload);
        if (response && response.id) {
          setCalendarId(response.id);
        }
      }
  
      if (response) {
        const newHolidays = newSettings.holidays || holidays;
        
        const holidaysResponse = await getRequest(`https://pms-api.ilizien-projects-cdf.in/transaction/holiday/`);
        const currentHolidays = holidaysResponse?.status === "success" ? 
          holidaysResponse.data.filter(h => h.calendar === calendarId) : [];
        
        // Replace delete with post + status_db:3
        for (const holiday of currentHolidays) {
          if (!newHolidays.some(h => h.id === holiday.id)) {
            await postRequest('https://pms-api.ilizien-projects-cdf.in/transaction/holiday/', {
              id: holiday.id,
              status_db: 3  // Mark for deletion
            });
          }
        }
        
        for (const holiday of newHolidays) {
          if (!holiday.id) {
            await postRequest('https://pms-api.ilizien-projects-cdf.in/transaction/holiday/', {
              calendar: calendarId,
              name: holiday.name,
              date: holiday.date
            });
          } else {
            // Update existing holiday with post
            await postRequest('https://pms-api.ilizien-projects-cdf.in/transaction/holiday/', {
              id: holiday.id,
              name: holiday.name,
              date: holiday.date
            });
          }
        }
  
        fetchCalendarDetails(selectedProject);
        setIsEditing(false);
        toast.success('Calendar settings saved successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving calendar settings:', error);
      toast.error('Failed to save calendar settings');
      return false;
    }
  };

  const handleAddProject = async (projectName) => {
    try {
      toast.success(`Project "${projectName}" created successfully`);
      const response = await getRequest('master/projects/');
      if (response.results?.status === 'success') {
        setProjects(response.results.data);
      }
    } catch (error) {
      console.error('Error fetching projects after add:', error);
      toast.error('Failed to create new project');
    }
  };

  const handleAddHoliday = async (date, name, holidayId = null) => {
    try {
      if (holidayId) {
        // Replace put with post + id
        const response = await postRequest(
          `https://pms-api.ilizien-projects-cdf.in/transaction/holiday/`,
          { id: holidayId, name, date }
        );
        toast.success(`Holiday "${name}" updated successfully`);
      } else {
        if (!calendarId) {
          const payload = {
            project: selectedProject,
            name: calendarName || 'Standard Calendar',
            working_days: workdays.length ? workdays : [1, 2, 3, 4, 5],
            working_hours: workingHours
          };
          const calendarResponse = await postRequest('transaction/calendar/', payload);
          if (calendarResponse && calendarResponse.id) {
            setCalendarId(calendarResponse.id);
            await postRequest('https://pms-api.ilizien-projects-cdf.in/transaction/holiday/', {
              calendar: calendarResponse.id,
              name: name,
              date: date
            });
            toast.success(`Holiday "${name}" added successfully`);
          }
        } else {
          await postRequest('https://pms-api.ilizien-projects-cdf.in/transaction/holiday/', {
            calendar: calendarId,
            name: name,
            date: date
          });
          toast.success(`Holiday "${name}" added successfully`);
        }
      }
      fetchCalendarDetails(selectedProject);
    } catch (error) {
      console.error('Error adding/updating holiday:', error);
      toast.error('Failed to add/update holiday');
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => prev === 0 ? (setCurrentYear(prevYear => prevYear - 1), 11) : prev - 1);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => prev === 11 ? (setCurrentYear(prevYear => prevYear + 1), 0) : prev + 1);
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!holidayId) {
      toast.error('Cannot delete holiday - missing ID');
      return;
    }
  
    try {
      // Replace delete with post + status_db:3
      const response = await postRequest(
        `https://pms-api.ilizien-projects-cdf.in/transaction/holiday/`,
        { id: holidayId, status_db: 3 }
      );
  
     if (response) { 
  setHolidays(prev => prev.filter(h => h.id !== holidayId));
  toast.success('Holiday deleted successfully');
  fetchCalendarDetails(selectedProject);
} else {
  throw new Error('Failed to get valid response');
}
    } catch (error) {
      console.error('Delete holiday error:', error);
      toast.error(`Delete: ${error.response?.data?.message || error.message}`);
    } finally {
      setShowDeleteDialog(false);
      setHolidayToDelete(null);
    }
  };

  const confirmDeleteHoliday = (holidayId) => {
    setHolidayToDelete(holidayId);
    setShowDeleteDialog(true);
  };

  const deleteHoliday = (holidayId) => {
    confirmDeleteHoliday(holidayId);
  };

  const currentProject = projects.find(p => p.id === selectedProject);

  return (
    <div className="min-h-screen bg-gray-100">
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <ReusableDeleteDialog
        visible={showDeleteDialog}
        onHide={() => {
          setShowDeleteDialog(false);
          setHolidayToDelete(null);
        }}
        onDelete={() => handleDeleteHoliday(holidayToDelete)}
        title="Delete Holiday"
        description="Are you sure you want to delete this holiday? This action cannot be undone."
        deleteLabel="Delete Holiday"
      />
      
      <div className="max-w-full mx-auto p-4">
        <div className="bg-blue-600 text-white p-6 rounded-t-lg shadow-lg">
          <div className="flex justify-center items-center">
            <div className="flex items-center space-x-3">
              <Calendar className="h-8 w-8" />
              <h1 className="text-3xl font-bold">Holiday Calendar</h1>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-b-lg shadow-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="relative">
              <div 
                className="flex justify-between items-center w-full p-2 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-500"
                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
              >
                <div>
                  <div className="text-sm font-medium text-gray-700">Selected Project</div>
                  <div className="font-semibold text-blue-700">
                    {isLoading ? 'Loading...' : currentProject?.name || 'No projects available'}
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-blue-500" />
              </div>
              
              {showProjectDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {projects.map(project => (
                    <div 
                      key={project.id} 
                      className="p-2 hover:bg-blue-50 cursor-pointer"
                      onClick={() => {
                        setSelectedProject(project.id);
                        setShowProjectDropdown(false);
                      }}
                    >
                      {project.name}
                    </div>
                  ))}
                  <div 
                    className="p-2 border-t border-gray-200 text-blue-600 hover:bg-blue-50 flex items-center space-x-1 cursor-pointer"
                    onClick={() => {
                      setShowAddProjectModal(true);
                      setShowProjectDropdown(false);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add New Project</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end items-center">
              <button 
                onClick={() => setShowEditModal(true)} 
                className="flex items-center space-x-1 bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg text-gray-700"
              >
                <Settings className="h-4 w-4" />
                <span>Calendar Settings</span>
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg cursor-pointer">
                <Upload className="h-4 w-4" />
                <span>Upload File</span>
                <input 
                  type="file" 
                  accept=".json" 
                  className="hidden" 
                  onChange={handleFileUpload} 
                />
              </label>

              {isEditing ? (
                <button 
                  onClick={() => handleSaveSettings({
                    name: calendarName,
                    workdays: workdays,
                    workingHours: workingHours,
                    holidays: holidays
                  })} 
                  className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                >
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </button>
              ) : (
                <button 
                  onClick={() => setIsEditing(true)} 
                  className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  <Edit className="h-4 w-4" />
                  <span>Edit Calendar</span>
                </button>
              )}
            </div>
          </div>

          <div className="mb-4 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handlePreviousMonth}
                className="p-2 rounded-full hover:bg-gray-200"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <h2 className="text-xl font-bold">
                {new Date(currentYear, currentMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              
              <button 
                onClick={handleNextMonth}
                className="p-2 rounded-full hover:bg-gray-200"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          <CalendarGrid
            currentMonth={currentMonth}
            currentYear={currentYear}
            workdays={workdays}
            holidays={holidays}
            onDayClick={handleDayClick}
            isEditing={isEditing}
          />

          {isEditing && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <span className="font-bold">Edit Mode Active:</span> Click on any day to add or edit a holiday. Use the calendar settings to manage working days and hours.
              </p>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="font-bold text-xl mb-3 text-blue-700">{calendarName || 'Calendar Details'}</h3>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <p className="mb-1"><span className="font-medium">Working Days:</span> {workdays.map(day => {
              const days = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
              return days[day];
            }).join(', ')}</p>
            <p className="mb-1"><span className="font-medium">Working Hours:</span> {workingHours.start} to {workingHours.end}</p>
            <p className="mb-1"><span className="font-medium">Total Holidays:</span> {holidays.length} days</p>
            <div className="mt-3">
              <h4 className="font-medium mb-2">Holiday Dates:</h4>
              <div className="flex flex-wrap gap-2">
              {holidays.sort((a, b) => a.date.localeCompare(b.date)).map(holiday => {
                const [year, month, day] = holiday.date.split('-');
                const displayDate = new Date(year, month - 1, day);
                return (
                  <div key={holiday.date} className="relative inline-block">
                    <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                      {displayDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} - {holiday.name}
                    </span>
                    {isEditing && holiday.id && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteHoliday(holiday.id);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        style={{ width: '20px', height: '20px' }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
              {holidays.length === 0 && <span className="text-gray-500 italic">No holidays set</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        calendarName={calendarName}
        workdays={workdays}
        workingHours={workingHours}
        holidays={holidays}
        onSave={handleSaveSettings}
        calendarId={calendarId}
      />

      <AddProjectModal
        isOpen={showAddProjectModal}
        onClose={() => setShowAddProjectModal(false)}
        onAddProject={handleAddProject}
      />

      <AddHolidayModal
        isOpen={showAddHolidayModal}
        onClose={() => {
          setShowAddHolidayModal(false);
          setSelectedDateForHoliday('');
        }}
        onAddHoliday={handleAddHoliday}
        onDeleteHoliday={deleteHoliday}
        calendarId={calendarId}
        selectedDate={selectedDateForHoliday}
        existingHoliday={holidays.find(h => h.date === selectedDateForHoliday)}
      />
    </div>
  );
};

export default ShipyardHolidayCalendar;